import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Webauthentication } from '@app/models/webauthentication';
import { UserCredentials } from '@app/models/user-credentials';
import { ArisJmsMessage } from '@app/models/aris-jms-message';
import { Mxna } from '@app/models/mxna';
import { SetupResponse } from '@app/models/setup-response';
import { MxposTrade } from '@app/models/mxpostrade';
import { RefreshToken } from '@app/models/refresh-token';
import { PersonPortalState } from '@app/models/person-portal-state';
import { EmailTemplate } from '@app/models/email-template';
import { EmailStatus } from '@app/models/email-status';
import { UseNotice } from '@app/models/use-notice';
import { SpotPrice } from '@app/models/spot-price';
import { LatestPrices } from '@app/models/latest-prices';
import { Mxstoplimit } from '@app/models/mxstoplimit';
import { CustomerDocument } from '@app/models/customer-document';
import { environment } from '@environments/environment';
import { HttpStatusCodes } from '@app/utilities/http-status-codes';
import { Router } from '@angular/router';
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { CustomerAgreement } from '@app/models/customer-agreement';
import { ValidationRequest } from '@app/models/validation-request'
import { AccountValues } from '@app/models/account-values';
import { LogEvent } from '@app/models/log-event';
import { AgreementStatus } from "@app/enumerations/agreement-status";
import { VoiceConnectionDetails } from "@app/models/voice-connection-details";
import { Hellosign } from "@app/models/hellosign";
import { HellosignSigningUrl } from "@app/models/hellosign-signing-url";


@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {

  constructor(private http: HttpClient, private router: Router) { }

  /**
   * Note that user's token (JWT) is saved in local storage.
   * @param userCredentials 
   */
  public login(userCredentials: UserCredentials): Observable<Webauthentication> {
    // Returns a JSON object!
    return this.http.post<Webauthentication>(`${environment.apiUrl}/api/v1/login`, JSON.stringify(userCredentials), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        map((webauthentication: Webauthentication) => {
          console.log(JSON.stringify(webauthentication));
          
          // The above maps the JSON object to a TypeScript object.
          if (webauthentication.acctNum) {
            let personPortalState: PersonPortalState = new PersonPortalState();
            // This is the state object that the application requires to maintian
            // state when the user refreshes the browser's view.              
            personPortalState.id = webauthentication.id;
            personPortalState.co = webauthentication.co;
            personPortalState.acctNum = webauthentication.acctNum;
            personPortalState.userName = webauthentication.userName;
            personPortalState.micaAccountName = webauthentication.micaAccountName;
            //webauthentication.customerAgreementList = null;
            personPortalState.webauthenticationId = webauthentication;
            // Used to determine if the user logged in with his temporary password
            personPortalState.webauthenticationId.passwordExpirationTime = webauthentication.passwordExpirationTime;
            // These values are utilized in the "Authorization Guard" module.
            personPortalState.loggedIn = true;
            personPortalState.acceptedAgreement = AgreementStatus.ACCEPTED;
            // Set the user's authorization token (JWT)
            personPortalState.token = webauthentication.token;            
            LocalStorageUtilities.setPersonPortalState(personPortalState);
          }
          return webauthentication;
        }),
        catchError((error: HttpErrorResponse) => {
          // If 404 error is thrown, no Person was found in the database. If a 403 was thrown
          // the user's credentials could not be validated. This is handled in the Error Interceptor.
          // Examples.: HttpStatusCodes.NOT_FOUND, HttpStatusCodes.FORBIDDEN          
          return throwError(error);
        })
      );
  }

  public logout(): void { }

  /**
   * 
   * After saving the new Webauthtication record to the datastore, create the PersonPortalState object
   * and save it to local storage.
   * 
   * @param acctNum 
   * @param ssn 
   * @param emailAddr 
   */
  public setUp(acctNum: string, ssn: string, emailAddr: string): Observable<SetupResponse> {
    return this.http.get<SetupResponse>(`${environment.apiUrl}/api/v1/setup`, { params: { 'acctNum': acctNum, 'ssn': ssn, 'emailAddr': emailAddr }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map((setupResponse: SetupResponse) => {
        let webauthentication: Webauthentication = setupResponse.webauthentication;
        let personPortalState: PersonPortalState = new PersonPortalState();
        // This is the state object that the application requires to maintian its
        // state when the user refreshes the browser.              
        personPortalState.id = webauthentication.id;
        personPortalState.co = webauthentication.co;
        personPortalState.acctNum = webauthentication.acctNum;
        personPortalState.userName = webauthentication.userName;
        personPortalState.micaAccountName = webauthentication.micaAccountName;
        personPortalState.webauthenticationId = webauthentication;
        // These values are utilized in "Authorization Guard" module.
        personPortalState.loggedIn = true;
        personPortalState.acceptedAgreement = AgreementStatus.NONE;
        personPortalState.token = webauthentication.token;
        LocalStorageUtilities.setPersonPortalState(personPortalState);
        return setupResponse;
      }));
  }

  /**
   * Ask the server for a refreshed token.
   */
  public refreshToken(): Observable<RefreshToken> {
    return this.http.get<RefreshToken>(`${environment.apiUrl}/api/v1/refreshtoken`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map((token: RefreshToken) => {
        return token;
      }));
  }

  public getAndIncrementFailedCount(userId: string): Observable<number> {
    return this.http.get<number>(`${environment.apiUrl}/api/v1/failedlogincount`, { params: { 'userId': userId }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map((failedLoginCount: number) => {
        return failedLoginCount;
      }));
  }

  public isAccountAccessDisabled(userId: string): Observable<boolean> {
    const url = `${environment.apiUrl}/api/v1/accountdisabled`;
    const finalUrl = `${url}?userId=${userId}`; // Construct the final URL with parameters

    console.log(userId);
    console.log(url); // Log base URL
    console.log(finalUrl); // Log the complete URL with query parameters
    // return this.http.get<boolean>('http://localhost:8080/api/v1/accountdisabled?userId=bjenkins@monex.com')
    // .pipe(map((isDisabled: boolean) => {
    //   return isDisabled
    // }));
    return this.http.get<boolean>(`${environment.apiUrl}/api/v1/accountdisabled`, { params: { 'userId': userId }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map((isDisabled: boolean) => {
        return isDisabled;
      }));
  }

  ///
  public emailUserId(mainAccount: string): Observable<Mxna> {
    let coAccount: string = "H" + mainAccount;
    return this.http.get<Mxna>(`${environment.apiUrl}/api/v1/mxna/accounts`, { params: { 'mainAccount': coAccount }, headers: { 'Content-Type': 'application/json' } });
  }

  public findWebAuthenticationByAcct(acct: string): Observable<Webauthentication> {
    return this.http.get<Webauthentication>(`${environment.apiUrl}/acct`, { params: { 'acctNum': acct }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map(webauthentication => {
        // Map JSON object to a TypeScript object                               
        let p = new Webauthentication().deserialize(webauthentication);
        return p;
      }),
        catchError((error: HttpErrorResponse) => {
          // If 404 error is thrown, no Webauthentication record was found in the database.  This is handled in the Error Interceptor.
          if (error.status === HttpStatusCodes.NOT_FOUND) {
            return of(new Webauthentication());
          }
          return throwError("Error thrown from catchError: " + error.type);
        })
      );
  }

  /**
   * Called when the person is attepting to activate their account.  Specifically, if no WebAuthenticaton
   * records exists on the server, this creates one.  Going forward, the user is unable to activate their
   * account because a WebAuthentication recoard already exists.
   * 
   * @param webauthentication    
   */
  public saveWebAuthenticaton(webauthentication: Webauthentication): Observable<Webauthentication> {

    return this.http.post<Webauthentication>(`${environment.apiUrl}/api/v1/webauthentication`, JSON.stringify(webauthentication), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(webauthentication => {
        // After a WebAuthentication record has been created, user the response to set the application's
        // state.   
        if (webauthentication) {
          // This is the state object that the application requires to maintian
          // state when the user refreshes the browser view.
          let personPortalState: PersonPortalState = new PersonPortalState();
          personPortalState.id = webauthentication.id;
          personPortalState.co = webauthentication.co;
          personPortalState.acctNum = webauthentication.acctNum;
          personPortalState.userName = webauthentication.userName;
          personPortalState.micaAccountName = webauthentication.micaAccountName;
          personPortalState.loggedIn = true;
          // This is needed because the record will need to be updated when the person
          // accepts the site agreement.  Note: we don't want to have the WebAuthentication
          // object hanging around any longer than it has to so we'll use the id to look it up.         
          personPortalState.webauthenticationId = webauthentication;
          // Store the PersonPortalState object in local storage to keep user logged in between page refreshes  
          // TODO: Decide if clearing is necessary.
          LocalStorageUtilities.clearPersonPortalState();
          LocalStorageUtilities.setPersonPortalState(personPortalState);
          // Map JSON object to a TypeScript object               
          let w: Webauthentication = new Webauthentication().deserialize(webauthentication);
          return w;
        } else {
        }
      }));
  }

  /**
   * TODO: Determine if this is used.
   * 
   * @param webauthentication 
   * 
   * @param id 
   */
  public updateWebAuthenticaton(webauthentication: Webauthentication, id: number): Observable<Webauthentication> {
    return this.http.put<Webauthentication>(`${environment.apiUrl}/api/v1/webauthentication/` + id, JSON.stringify(webauthentication), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(webauthentication => {
        // After a WebAuthentication record has been created, return the response so as to set the application's
        // new state.   
        if (webauthentication) {
          return webauthentication;
        }
      }));
  }

  /**
   * Called from the Utilities menu after the user has already logged in.
   * 
   * @param id 
   * @param password 
   */
  public savePassword(id: number, password: string): Observable<Webauthentication> {
    return this.http.post<Webauthentication>(`${environment.apiUrl}/api/v1/password/` + id + `/${password}`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(webauthentication => {
        return webauthentication;
      }));
  }

  /**
   * Webauthentication update for User ID changes.  
   * 
   * @param webauthentication 
   * @param id 
   */
  // public updateUserId(webauthentication: Webauthentication, id: number): Observable<Webauthentication> {
  //   return this.http.put<Webauthentication>(`${environment.apiUrl}/api/v1/userid/` + id, JSON.stringify(webauthentication), { headers: { 'Content-Type': 'application/json' } })
  //     .pipe(
  //       map((webauthentication: Webauthentication) => {
  //         // After the successful update, return the response so that the application's state 
  //         // can be updated.
  //         if (webauthentication) {
  //           return webauthentication;
  //         }
  //       }));
  // }

  /**
   * Saves the user's new User ID.
   * 
   * @param webauthentication    
   * @param id 
   */
  public saveUserId(webauthentication: Webauthentication, id: number): Observable<Webauthentication> {
    let data = new HttpParams()
      .set('userName', webauthentication.userName)
      .set('id', `${id}`);
    return this.http.post<Webauthentication>(`${environment.apiUrl}/api/v1/saveuserid`, data)
      .pipe(map((webauthentication: Webauthentication) => {
        // After the successful update, return the response so that the application's state 
        // can be updated.
        if (webauthentication) {
          return webauthentication;
        }
      }));
  }

  /**
   * After the user accepts the site's use agreement, the agreement information is saved
   * to the "Customer Agreement" table.
   * 
   * 2020-01-21
   * 
   * @deprecated
   * 
   * @param customerAgreement  
   */
  public saveCustomerAgreement(customerAgreement: CustomerAgreement): Observable<CustomerAgreement> {
    return this.http.post<CustomerAgreement>(`${environment.apiUrl}/api/v1/customeragreement`, JSON.stringify(customerAgreement), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(customerAgreement => {
        // After a WebAuthentication record has been created, use the response to set the application's
        // state.               
        if (customerAgreement) {
          return customerAgreement;
        } else {
          console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>> Can't save agreement info.");
        }
      }));
  }

  public updateWebauthorizationWithCustomerAgreement(customerAgreement: CustomerAgreement, id: number): Observable<Webauthentication> {
    return this.http.put<Webauthentication>(`${environment.apiUrl}/api/v1/customeragreement/` + id, JSON.stringify(customerAgreement), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(webauthentication => {
        // After a WebAuthentication record has been created, use the response to set the application's
        // state.               
        if (webauthentication) {
          return webauthentication;
        } else {
          console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>> Can't save agreement info.");
        }
      }));
  }

  public saveValidationRequest(validationRequest: ValidationRequest): Observable<ValidationRequest> {
    return this.http.post<ValidationRequest>(`${environment.apiUrl}/api/v1/validationrequest`, JSON.stringify(validationRequest), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(validationRequest => {
        // After a WebAuthentication record has been created, user the response to set the application's
        // state.               
        if (validationRequest) {
          return validationRequest;
        } else {
        }
      }));
  }

  public findMxnaById(id: string): Observable<Mxna> {
    let coAccount: string = "H" + id;
    return this.http.get<Mxna>(`${environment.apiUrl}/api/v1/mxna`, { params: { 'id': coAccount }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map(mxna => {
        return mxna;
      }));
  }

  /**
   * Initial version.
   * 2020-02-04
   * Returns all positions for a given company and account combination.
   * 
   * 
   * @param mainAccount 
   */
  public findMxposTradesByCoAccount(mainAccount: string): Observable<MxposTrade[]> {
    let coAccount: string = "H" + mainAccount;
    return this.http.get<MxposTrade[]>(`${environment.apiUrl}/api/v1/mxna/positions`, { params: { 'coAccount': coAccount }, headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * Initial version.
   * 2020-02-04
   * Returns all stops and limits for a given company and account combination.
   * 
   * 
   * @param mainAccount 
   */
  public findMxstoplimitByAccount(account: string): Observable<Mxstoplimit[]> {
    return this.http.get<Mxstoplimit[]>(`${environment.apiUrl}/api/v1/mxna/stoplimits`, { params: { 'account': account }, headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * Returns all of a person's accounts, including subaccounts.
   * @param mainAccount 
   */
  public findAccountsByMainAccount(mainAccount: string): Observable<Mxna[]> {
    let coAccount: string = "H" + mainAccount;
    return this.http.get<Mxna[]>(`${environment.apiUrl}/api/v1/mxna/accounts`, { params: { 'mainAccount': coAccount }, headers: { 'Content-Type': 'application/json' } });
  }

  public findEmailTemplateByEmailId(id: number): Observable<EmailTemplate> {
    let templateId: string = String(id);
    return this.http.get<EmailTemplate>(`${environment.apiUrl}/api/v1/emailtemplate`, { params: { 'emailId': templateId }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map(template => {
        return template;
      }));
  }

  public findLatestUseNotice(): Observable<UseNotice> {
    return this.http.get<UseNotice>(`${environment.apiUrl}/api/v1/usenotice`, { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(useNotice => {
        // Map JSON object to a TypeScript object                               
        let e = new UseNotice().deserialize(useNotice);
        return e;
      }));
  }

  /**
   * 
   * Used only for sending the customer his User ID.  It's easiest to have this done
   * on the server side (for now).
   * 
   * @param mainAccount 
   * @param templateId 
   */
  public sendIdEmailToCustomer(mainAccount: string, templateId: number): Observable<any> {
    let data = new HttpParams()
      .set('mainAccount', mainAccount)
      .set('templateId', `${templateId}`);
    return this.http.post<any>(`${environment.apiUrl}/api/v1/email/sendcustomeridemail`, data, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      .pipe(map((responseCode: EmailStatus) => {
        return responseCode;
      }));
  }

  /**
   * This is new and should the method below.
   * 
   * @param subject 
   * @param mainAccount 
   * @param userId 
   */
  public sendEmailToAdmin(subject: string, mainAccount?: string, userId?: string): Observable<any> {
    let data = new HttpParams()
      .set('subject', subject)
      .set('mainAccount', mainAccount)
      .set('userId', userId)
    return this.http.post<any>(`${environment.apiUrl}/api/v1/email/sendadminemail`, data, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      .pipe(map((responseCode: EmailStatus) => {
        return responseCode;
      }));
  }

  /**
   * 
   * Used only for sending the customer his new password.  It's easiest to have this done
   * on the server side (for now).
   * 
   * @param userId 
   * @param templateId 
   */
  public sendPasswordEmailToCustomer(userId: string, templateId: number): Observable<any> {
    let data = new HttpParams()
      .set('userId', userId)
      .set('templateId', `${templateId}`);
    return this.http.post<any>(`${environment.apiUrl}/api/v1/email/sendpasswordcustomeremail`, data, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } })
      .pipe(map((responseCode: EmailStatus) => {
        return responseCode;
      }));
  }

  public sendCustomerEmail(address: string, subject: string, html: string): Observable<EmailStatus> {
    return this.http.post<EmailStatus>(`${environment.apiUrl}/api/v1/email/customer`, JSON.stringify({
      body: html
    }),
      { params: { 'address': address, 'subject': subject }, headers: { 'Content-Type': 'application/json' } })
      .pipe(map((responseCode: EmailStatus) => {
        return responseCode;
      }));
  }

  /**
   * Send object with only enough information to calculate the account's summary values.
   */
  // public getCustomerAccountValues(mxna: Mxna): Observable<AccountValues> {
  //   let parameters: AccountValuesParameters = new AccountValuesParameters(mxna);
  //   return this.http.post<AccountValues>(`${environment.apiUrl}/api/v1/accountvalues`, JSON.stringify(parameters), { headers: { 'Content-Type': 'application/json' } })
  //     .pipe(map(accountValues => {
  //       // After a WebAuthentication record has been created, use the response to set the application's
  //       // state.               
  //       if (accountValues) {
  //         return accountValues;
  //       } else {
  //         console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>> Can't save agreement info.");
  //       }
  //     }));
  // }

  public getMxole(accountNumber: string): Observable<AccountValues> {
    return this.http.post<AccountValues>(`${environment.apiUrl}/api/v1/mxole`, JSON.stringify({
      account: accountNumber
    }), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(accountValues => {
        // After a WebAuthentication record has been created, use the response to set the application's
        // state.               
        if (accountValues) {
          return accountValues;
        } else {
          console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>> Can't save agreement info.");
        }
      }));
  }

  public findDelayedPrices(): Observable<SpotPrice[]> {
    return this.http.get<SpotPrice[]>(`${environment.apiUrl}/api/v1/delayedprices`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findLatestPrices(): Observable<LatestPrices[]> {
    return this.http.get<LatestPrices[]>(`${environment.apiUrl}/api/v1/latestprices`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findSpotPrices(): Observable<SpotPrice[]> {
    return this.http.get<SpotPrice[]>(`${environment.apiUrl}/api/v1/spotprices`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findHistoricalGoldPrices(): Observable<[number, number][]> {
    return this.http.get<[number, number][]>(`${environment.apiUrl}/api/v1/historicalprices/gold`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findHistoricalSilverPrices(): Observable<[number, number][]> {
    return this.http.get<[number, number][]>(`${environment.apiUrl}/api/v1/historicalprices/silver`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findHistoricalPlatinumPrices(): Observable<[number, number][]> {
    return this.http.get<[number, number][]>(`${environment.apiUrl}/api/v1/historicalprices/platinum`, { headers: { 'Content-Type': 'application/json' } });
  }

  public findHistoricalPalladiumPrices(): Observable<[number, number][]> {
    return this.http.get<[number, number][]>(`${environment.apiUrl}/api/v1/historicalprices/palladium`, { headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * 2020-02-04
   * Returns all of a person's documents.
   * 
   */
  public findDocuments(co: string, acctNum: string): Observable<CustomerDocument[]> {
    return this.http.get<CustomerDocument[]>(`${environment.apiUrl}/api/v1/documents`, { params: { 'co': co, 'acctNum': acctNum }, headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * 2020-09-03
   * Save customer website event to the datastore.
   * 
   */
  public saveLogEvent(logEvent: LogEvent): Observable<LogEvent> {
    return this.http.post<LogEvent>(`${environment.apiUrl}/api/v1/siteactivitylog`, JSON.stringify(logEvent), { headers: { 'Content-Type': 'application/json' } })
      .pipe(map(logEvent => {
        if (logEvent) {
          return logEvent;
        } else {
          console.log("Logging of event failed.");

        }
      }));
  }

  public sendArisJmsMessage(arisJmsMessage: ArisJmsMessage): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/v1/arisjmsmessage`, JSON.stringify(arisJmsMessage), { headers: { 'Content-Type': 'application/json' } });
  }

  public intiateCallToCustomer(voiceConnectionDetails: VoiceConnectionDetails): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/v1/initiatecall`, JSON.stringify(voiceConnectionDetails), { headers: { 'Content-Type': 'application/json' } });
  }

  public getESignDocuments(): Observable<any> {      
    return this.http.get<HellosignSigningUrl>(`${environment.apiUrl}/api/v1/esigndocuments`, { headers: { 'Content-Type': 'application/json' } });
  }

  /**
   * Hello Sign signature request.
   * 
   * @param name 
   * @param emailAddress 
   */
  public getSignatureRequest(name: string, emailAddress: string, templateId: string, account: string, accountRep: string): Observable<any> {
    let data = new HttpParams()
      .set('name', name)
      .set('emailAddress', emailAddress)
      .set('templateId', templateId)
      .set('account', account)
      .set('accountRep', accountRep);
    return this.http.post<Hellosign>(`${environment.apiUrl}/api/v1/hellosign/signaturerequest`, data);
  }

  /**
   * Hello Sign embedded signing URL
   * 
   * @param signatureId 
   */
  public getSigningUrl(signatureId: string, ): Observable<any> {
    let data = new HttpParams()
      .set('signatureId', signatureId)     
    return this.http.post<HellosignSigningUrl>(`${environment.apiUrl}/api/v1/hellosign/signingurl`, data);
  }

}
